import {
  ArrayParam,
  BooleanParam,
  NumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault,
} from 'use-query-params';
import { useCallback } from 'react';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';

type QueryArrayValue = string | number | null;

const areArraysEqual = (
  left: QueryArrayValue[] | undefined,
  right: QueryArrayValue[] | undefined,
) => {
  if (left === right) return true;
  if (!left || !right || left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const serializeArrayValue = (value: QueryArrayValue[] | undefined) => {
  const normalized = value?.filter((item): item is string | number => item !== null) ?? [];
  return normalized.length ? normalized.join(',') : undefined;
};

const trackFilterChange = (filterName: string, value?: string | number | boolean) => {
  datadogActions.trackDatadogAction(DATADOG_EVENT.cabinetTableFilterChanged, {
    filterName,
    value,
    pathname: window.location.pathname,
  });
};

const useListParams = () => {
  const [q, setQRaw] = useQueryParam('q', withDefault(StringParam, undefined));
  const [status, setStatusRaw] = useQueryParam(
    'status',
    withDefault(StringParam, undefined)
  );
  const [apps, setAppsRaw] = useQueryParam(
    'apps',
    withDefault(ArrayParam, undefined)
  );

  const [packs, setPacksRaw] = useQueryParam(
    'pack',
    withDefault(NumericArrayParam, [])
  );
  const [orderBy, setOrderByRaw] = useQueryParam(
    'order_by',
    withDefault(StringParam, 'created')
  );
  const [desc, setDescRaw] = useQueryParam(
    'desc',
    withDefault(BooleanParam, true)
  );
  const [date, setDateRaw] = useQueryParam(
    'date',
    withDefault(StringParam, undefined)
  );

  const setQ = useCallback((nextValue: string | undefined) => {
    if (q === nextValue) {
      return;
    }
    trackFilterChange('q', nextValue);
    setQRaw(nextValue);
  }, [q, setQRaw]);

  const setStatus = useCallback((nextValue: string | undefined) => {
    if (status === nextValue) {
      return;
    }
    trackFilterChange('status', nextValue);
    setStatusRaw(nextValue);
  }, [setStatusRaw, status]);

  const setApps = useCallback((nextValue: (string | null)[] | undefined) => {
    if (areArraysEqual(apps, nextValue)) {
      return;
    }
    trackFilterChange('apps', serializeArrayValue(nextValue));
    setAppsRaw(nextValue);
  }, [apps, setAppsRaw]);

  const setPacks = useCallback((nextValue: (number | null)[]) => {
    if (areArraysEqual(packs, nextValue)) {
      return;
    }
    trackFilterChange('pack', serializeArrayValue(nextValue));
    setPacksRaw(nextValue);
  }, [packs, setPacksRaw]);

  const setOrderBy = useCallback((nextValue: string | undefined) => {
    if (orderBy === nextValue) {
      return;
    }
    trackFilterChange('order_by', nextValue);
    setOrderByRaw(nextValue);
  }, [orderBy, setOrderByRaw]);

  const setDesc = useCallback((nextValue: boolean | undefined) => {
    if (desc === nextValue) {
      return;
    }
    trackFilterChange('desc', nextValue);
    setDescRaw(nextValue);
  }, [desc, setDescRaw]);

  const setDate = useCallback((nextValue: string | null | undefined) => {
    const normalizedValue = nextValue ?? undefined;
    if (date === normalizedValue) {
      return;
    }
    trackFilterChange('date', normalizedValue);
    setDateRaw(normalizedValue);
  }, [date, setDateRaw]);

  return {
    q,
    setQ,
    packs,
    setPacks,
    apps,
    setApps,
    status,
    setStatus,
    date,
    setDate,
    orderBy,
    setOrderBy,
    desc,
    setDesc,
  };
};

export default useListParams;
